import { Link, Theme, Tooltip, Typography } from '@mui/material';
import type { Property } from 'csstype';
import pluralize from 'pluralize';
import { FC } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { AppPages } from '../../common/routing/app-navigator';
import StatButton from '../../elements/StatButton/StatButton';
import { DashboardFilter, InventoryFilterType } from '../inventory-types';
import InventoryTooltipContent from './InventoryTooltipContent';

const DashboardInventory: FC<
  React.PropsWithChildren<DashboardInventoryProps>
> = ({
  apps,
  unRelevantApps,
  appliedFilters,
  filters,
  onInventoryFilterClick,
  hideAppsCount = false,
}) => {
  const { classes, cx } = useStyles();
  const appsTitle = (
    <Typography
      fontWeight={700}
      data-testid='apps-count'
      className={cx(classes.appsText, classes.relevantAppCount)}>
      {pluralize('App', apps, true)}
    </Typography>
  );
  return (
    <div className={classes.dashboardInventory}>
      <div className={classes.textContainer}>
        <Typography
          lineHeight='25px'
          fontWeight={400}
          variant='h1'
          data-testid='inventory-text'
          color='text.primary'>
          What I am Protecting
        </Typography>
      </div>
      {apps > 0 &&
        (hideAppsCount ? (
          appsTitle
        ) : (
          <Link
            underline='none'
            component={RouterLink}
            to={AppPages.Applications}>
            {appsTitle}
          </Link>
        ))}

      {unRelevantApps > 0 && (
        <Typography className={classes.appsTextIrrelevant}>
          <span>{unRelevantApps} Irrelevant</span>
          <RouterLink to={AppPages.IrrelevantApps}>
            <FaExternalLinkAlt
              className={classes.notRelevantAppIcon}
              style={{ color: '#585cfc' }}
            />
          </RouterLink>
        </Typography>
      )}
      <div className={classes.filtersContainer}>
        {filters.map((f, index) => (
          <Tooltip
            key={index}
            placement='top-start'
            title={
              f.tooltip.length > 0 && (
                <InventoryTooltipContent apps={f.tooltip} />
              )
            }
            arrow>
            <span>
              <StatButton
                count={f.count}
                label={f.label}
                delta={f.delta}
                trend={f.trend}
                disabled={f.count === 0}
                active={appliedFilters.includes(f.type)}
                onClick={() => onInventoryFilterClick(f.type)}
              />
            </span>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

const useStyles = makeStyles<void, 'dashboardInventory'>()((theme: Theme) => ({
  dashboardInventory: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBlock: theme.spacing(1),
    paddingInline: theme.spacing(2.5),
    gap: theme.spacing(2),
    minHeight: 60,
    [theme.breakpoints.up('md')]: {
      alignItems: 'center',
      flexDirection: 'row' as Property.FlexDirection,
    },
  },
  textContainer: {
    whiteSpace: 'nowrap',
    paddingInlineEnd: theme.spacing(2),
  },
  relevantAppCount: {
    color: '#5059FF',
    textAlign: 'center',
    padding: theme.spacing(0.5, 0.5),
    background:
      theme.palette.mode === 'dark'
        ? '#1C1F2A'
        : theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  notRelevantAppIcon: {
    fontSize: 8,
    cursor: 'pointer',
    width: '1rem',
    marginLeft: '3px',
  },
  appsTextIrrelevant: {
    marginRight: '10',
    color: `${theme.palette.dashInventory.textColor.colorRelevant} !important`,
    font: 'Roboto',
    fontSize: '0.875rem',
  },
  appsText: {
    color: `${theme.palette.dashInventory.textColor.colorAppsText} !important`,
    whiteSpace: 'nowrap',
    fontSize: 14,
    minWidth: '5rem',
  },

  filtersContainer: {
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
}));

export interface DashboardInventoryProps {
  apps: number;
  unRelevantApps: number;
  appliedFilters: Array<InventoryFilterType>;
  filters: DashboardFilter[];
  onInventoryFilterClick: (filter: InventoryFilterType) => void;
  hideAppsCount?: boolean;
}

export default DashboardInventory;
